import React, { useEffect, useMemo } from "react";
import { useProfileStore } from "../../store/profileStore";
import { OCRExtractionModal } from "./OCRExtractionModal";
import type { DocumentItem, OCRExtraction } from "../../types";

/**
 * Component that manages the OCR extraction selection modal.
 * When entering review mode with a document that has multiple extractions
 * and no extraction is currently selected, this prompts the user to choose.
 */
export const ExtractionSelectionManager: React.FC = () => {
  const {
    documents,
    selectedDoc,
    review,
    selectedExtractionIds,
    setSelectedExtractionId,
    pendingExtractionSelection,
    setPendingExtractionSelection,
    clearPendingExtractionSelection,
  } = useProfileStore();

  // Find the document object from documents array
  const findDocument = (docId: string): DocumentItem | null => {
    return documents.find((doc) => doc.docId === docId) || null;
  };

  // Check if we need to show the modal when entering review mode
  useEffect(() => {
    // Only trigger when:
    // 1. Review mode is active
    // 2. A document is selected
    // 3. We haven't already shown the modal for this document
    // 4. The document has multiple extractions
    // 5. No extraction is currently selected for this document

    if (!review || !selectedDoc?.docId) {
      return;
    }

    const docId = selectedDoc.docId;
    const document = findDocument(docId);

    if (!document) {
      return;
    }

    const hasMultipleExtractions =
      document.ocrExtractions && document.ocrExtractions.length > 1;

    if (!hasMultipleExtractions) {
      return;
    }

    const alreadyHasSelection = selectedExtractionIds[docId] !== undefined;

    if (alreadyHasSelection) {
      return;
    }

    // Show the modal
    setPendingExtractionSelection(docId, document.label);
  }, [review, selectedDoc?.docId, documents, selectedExtractionIds]);

  // Get extractions for the pending document
  const pendingExtractions = useMemo((): OCRExtraction[] => {
    if (!pendingExtractionSelection.docId) {
      return [];
    }

    const document = findDocument(pendingExtractionSelection.docId);
    return document?.ocrExtractions || [];
  }, [pendingExtractionSelection.docId, documents]);

  const handleExtractionSelect = (extractionId: number) => {
    if (pendingExtractionSelection.docId) {
      setSelectedExtractionId(pendingExtractionSelection.docId, extractionId);
      clearPendingExtractionSelection();
    }
  };

  const handleClose = () => {
    // If user closes without selecting, auto-select the latest extraction
    if (pendingExtractionSelection.docId && pendingExtractions.length > 0) {
      const sortedExtractions = [...pendingExtractions].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setSelectedExtractionId(
        pendingExtractionSelection.docId,
        sortedExtractions[0].id
      );
    }
    clearPendingExtractionSelection();
  };

  return (
    <OCRExtractionModal
      opened={!!pendingExtractionSelection.docId}
      onClose={handleClose}
      extractions={pendingExtractions}
      onSelect={handleExtractionSelect}
      documentName={pendingExtractionSelection.documentName || ""}
    />
  );
};
